import { formatGBP } from '../money';
import { DateTime } from 'luxon';

/**
 * Backlog #30/#31: Odoo POS sales. Odoo supplies pricing and till sales only (never stock).
 *
 * Definitions (open questions for Daniel are in docs/odoo-sales.md):
 * - takings      = sum of pos.order.amount_total (VAT-inclusive) for paid/done/invoiced orders, refunds netted off.
 * - transaction  = one paid order with amount_total > 0. Refund orders (amount_total < 0) are counted separately.
 * - average basket = takings / transactions.
 */
export const SALES_ORDER_STATES = ['paid', 'done', 'invoiced'] as const;

export interface PosOrderLine { product_ref: string; product_name: string; qty: number; total: number }
export interface PosOrder {
  id: number;
  config_id: number;
  /** UTC, as Odoo stores it ("2026-10-14 09:12:00" or ISO). */
  date_order: string;
  /** In the company currency (GBP), VAT-inclusive. */
  amount_total: number;
  state: string;
  lines?: PosOrderLine[];
}

/** units (t485u): item quantity sold in the hour, refund lines netted off (negative qty). */
export interface HourlyRow { location_id: string; hour_start: string; takings_pence: number; transactions: number; refunds: number; refunds_pence: number; units: number }
export interface ProductDayRow { location_id: string; day: string; product_ref: string; product_name: string; qty: number; takings_pence: number }

export const toPence = (gbp: number) => Math.round(gbp * 100);

export function parseOdooDate(s: string): DateTime {
  const iso = s.includes('T') ? s : s.replace(' ', 'T');
  return DateTime.fromISO(iso, { zone: s.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(s) ? undefined : 'utc' }).toUTC();
}

/** Rolls orders up per building per hour (UTC hour boundaries) and per building per local day for products. Unmapped tills are skipped. */
export function aggregateOrders(orders: PosOrder[], mapping: Map<number, string>, tz: string): { hourly: HourlyRow[]; products: ProductDayRow[]; skipped: number } {
  const hourly = new Map<string, HourlyRow>();
  const products = new Map<string, ProductDayRow>();
  let skipped = 0;
  for (const o of orders) {
    const loc = mapping.get(o.config_id);
    if (!loc || !(SALES_ORDER_STATES as readonly string[]).includes(o.state)) { skipped++; continue; }
    const at = parseOdooDate(o.date_order);
    if (!at.isValid) { skipped++; continue; }
    const hour = at.startOf('hour').toISO()!;
    const key = `${loc}|${hour}`;
    const h = hourly.get(key) ?? { location_id: loc, hour_start: hour, takings_pence: 0, transactions: 0, refunds: 0, refunds_pence: 0, units: 0 };
    const p = toPence(o.amount_total);
    h.takings_pence += p;
    if (p > 0) h.transactions++;
    else if (p < 0) { h.refunds++; h.refunds_pence += -p; }
    hourly.set(key, h);
    const day = at.setZone(tz).toISODate()!;
    for (const l of o.lines ?? []) {
      // t485u: units are the item lines that go the same way as the order (a sale's discount line or a refund's
      // fee line isn't an item), so a refund's negative qty nets off what it returns.
      if (Math.sign(l.total) === Math.sign(p) || l.total === 0) h.units = Math.round((h.units + l.qty) * 100) / 100;
      const pk = `${loc}|${day}|${l.product_ref}`;
      const r = products.get(pk) ?? { location_id: loc, day, product_ref: l.product_ref, product_name: l.product_name, qty: 0, takings_pence: 0 };
      r.qty = Math.round((r.qty + l.qty) * 100) / 100;
      r.takings_pence += toPence(l.total);
      products.set(pk, r);
    }
  }
  return { hourly: [...hourly.values()], products: [...products.values()], skipped };
}

export const averageBasketPence = (takings: number, transactions: number) => (transactions > 0 ? Math.round(takings / transactions) : null);

// ---------------------------------------------------------------------------------------------
// Till-linked staffing
// ---------------------------------------------------------------------------------------------
export interface StaffingHourInput { hour_start: string; takings_pence: number; transactions: number; scheduled: number; clocked: number }
export type StaffingFlag = 'under' | 'over' | 'ok' | 'no_staff' | 'quiet';
export interface StaffingHour extends StaffingHourInput { tx_per_staff: number | null; flag: StaffingFlag }

/** Default comfortable throughput per person per hour, used until there's enough history. Admin-tunable later. */
export const DEFAULT_TX_PER_STAFF_HOUR = 12;
export const UNDER_FACTOR = 1.3;
export const OVER_FACTOR = 0.5;

/** The benchmark is the median transactions per clocked-in person over busy hours, so each building calibrates itself. */
export function staffingBenchmark(hours: StaffingHourInput[], fallback = DEFAULT_TX_PER_STAFF_HOUR): number {
  const v = hours.filter(h => h.clocked > 0 && h.transactions > 0).map(h => h.transactions / h.clocked).sort((a, b) => a - b);
  if (v.length < 8) return fallback;
  const m = Math.floor(v.length / 2);
  return Math.round((v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2) * 10) / 10;
}

export function flagStaffing(hours: StaffingHourInput[], benchmark: number): StaffingHour[] {
  return hours.map(h => {
    const staff = h.clocked || h.scheduled;
    if (!staff) return { ...h, tx_per_staff: null, flag: h.transactions > 0 ? 'no_staff' : 'quiet' };
    const per = Math.round((h.transactions / staff) * 10) / 10;
    let flag: StaffingFlag = 'ok';
    if (per > benchmark * UNDER_FACTOR) flag = 'under';
    else if (staff > 1 && per < benchmark * OVER_FACTOR) flag = 'over';
    return { ...h, tx_per_staff: per, flag };
  });
}

export interface StaffingSuggestion { weekday: number; hour: number; avg_transactions: number; suggested: number; currently_scheduled: number | null }

/**
 * Next week's staffing from recent weeks: average transactions for each weekday x local hour over the history,
 * divided by the benchmark, rounded up. Hours that never trade get no suggestion. weekday is ISO (1 = Monday).
 */
export function suggestStaffing(history: { hour_start: string; transactions: number }[], tz: string, benchmark: number, weeks: number): Omit<StaffingSuggestion, 'currently_scheduled'>[] {
  const sums = new Map<string, number>();
  for (const h of history) {
    const t = DateTime.fromISO(h.hour_start, { zone: 'utc' }).setZone(tz);
    const k = `${t.weekday}|${t.hour}`;
    sums.set(k, (sums.get(k) ?? 0) + h.transactions);
  }
  const out: Omit<StaffingSuggestion, 'currently_scheduled'>[] = [];
  for (const [k, total] of sums) {
    const [weekday, hour] = k.split('|').map(Number);
    const avg = Math.round((total / Math.max(1, weeks)) * 10) / 10;
    if (avg <= 0) continue;
    out.push({ weekday, hour, avg_transactions: avg, suggested: Math.max(1, Math.ceil(avg / Math.max(1, benchmark))) });
  }
  return out.sort((a, b) => a.weekday - b.weekday || a.hour - b.hour);
}

// ---------------------------------------------------------------------------------------------
// Targets and bonuses (#31)
// ---------------------------------------------------------------------------------------------
export type TargetPeriod = 'day' | 'week' | 'month';
export const TARGET_PERIODS: TargetPeriod[] = ['day', 'week', 'month'];
export const PERIOD_LABEL: Record<TargetPeriod, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

/** Local period containing the date: [start, end] inclusive ISO dates (weeks start Monday). */
export function periodRange(date: string, period: TargetPeriod): { start: string; end: string } {
  const d = DateTime.fromISO(date);
  const unit = period === 'day' ? 'day' : period === 'week' ? 'week' : 'month';
  return { start: d.startOf(unit).toISODate()!, end: d.endOf(unit).toISODate()! };
}

export interface BonusRule { kind: 'fixed' | 'percent'; amount_pence: number | null; percent: number | null }

/** fixed = flat amount per eligible person; percent = premium on the person's base pay for paid minutes in the period. */
export function bonusAmount(rule: BonusRule, paidMinutes: number, hourlyRatePence: number | null): { amount_pence: number; basis: string } {
  if (rule.kind === 'fixed') return { amount_pence: rule.amount_pence ?? 0, basis: `Fixed ${formatGBP(rule.amount_pence ?? 0)}` };
  const pct = rule.percent ?? 0;
  if (hourlyRatePence == null) return { amount_pence: 0, basis: `${pct}% premium - no hourly rate on file` };
  const amount = Math.round((paidMinutes / 60) * hourlyRatePence * (pct / 100));
  return { amount_pence: amount, basis: `${pct}% of ${(paidMinutes / 60).toFixed(2)} h at ${formatGBP(hourlyRatePence)}` };
}

/** Sales figures: whole pounds once they're round thousands ("£12,000"), pence otherwise. */
export const formatGbp = (pence: number | null | undefined) =>
  formatGBP(pence, { empty: '-', whole: pence != null && pence % 100 === 0 && Math.abs(pence) >= 100000 });

// ---------------------------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------------------------
/**
 * t485u: the home "Today's takings" widget when I can see sales at more than one building with a till: a total across
 * them (vs this time yesterday) and one row per building, highest takings first.
 */
export interface SalesAllBuildings {
  date: string; as_of: string | null;
  total: SalesFigures; yesterday_same_time: SalesFigures; change: SalesFiguresChange;
  buildings: { location_id: string; location_name: string; today: SalesFigures; target_pence: number | null; progress: number | null }[];
}
/**
 * t517u: the order of the takings rows: today's revenue inc. VAT, highest first, ties by building name; buildings with
 * no sales yet today go last, by name. Coerces with Number() so a string from the wire can't sort as text.
 */
export function sortTakingsRows<T extends { location_name: string; today: { revenue_pence: number | string } }>(rows: readonly T[]): T[] {
  const rev = (r: T) => Number(r.today.revenue_pence) || 0;
  const byName = (a: T, b: T) => a.location_name.localeCompare(b.location_name, 'en-GB', { sensitivity: 'base', numeric: true });
  return [...rows].sort((a, b) => {
    const ra = rev(a), rb = rev(b);
    if ((ra > 0) !== (rb > 0)) return ra > 0 ? -1 : 1;
    return ra > 0 ? rb - ra || byName(a, b) : byName(a, b);
  });
}
export interface SalesToday {
  /** t485u: set when I can see sales at 2+ buildings with a till (and didn't ask for one building). */
  all?: SalesAllBuildings | null;
  location_id: string; location_name: string; date: string;
  takings_pence: number; transactions: number; refunds: number; average_basket_pence: number | null;
  target_pence: number | null; progress: number | null;
  week: { takings_pence: number; target_pence: number | null }; month: { takings_pence: number; target_pence: number | null };
  top_sellers: { product_ref: string; product_name: string; qty: number; takings_pence: number }[];
  by_hour: { hour: number; takings_pence: number; transactions: number }[];
  last_synced_at: string | null;
}
/** t553u: the sales widgets that can be switched between buildings (Transactions, Top sellers). */
export type SalesWidgetPick = 'sales_transactions' | 'sales_top';
/** t553u: `location_id` value meaning every building I can see sales at, added together. */
export const SALES_ALL_BUILDINGS = 'all';
export const SALES_ALL_BUILDINGS_LABEL = 'All buildings';
/** t553u: where a widget's building choice is remembered on this device, per workspace, person and widget. */
export const salesWidgetPickKey = (ws: string, userId: string, widget: SalesWidgetPick) => `hub.sales.building:${ws}:${userId}:${widget}`;

/**
 * t553u: the buildings a sales widget can switch between: those with a till when any have one, else all I can see
 * sales at. The switch shows only with two or more, All buildings first.
 */
export function salesWidgetOptions(locations: readonly { id: string; name: string; till?: boolean }[]): { id: string; name: string }[] {
  const tills = locations.filter(l => l.till);
  const list = (tills.length ? tills : locations).map(l => ({ id: l.id, name: l.name }));
  return list.length > 1 ? [{ id: SALES_ALL_BUILDINGS, name: SALES_ALL_BUILDINGS_LABEL }, ...list] : list;
}

/**
 * t553u: which building a sales widget asks for. A remembered building I can still see wins; otherwise All buildings
 * (as Visitors does for two or more buildings, t534u). With one building the API answers it whatever is asked.
 */
export function salesWidgetChoice(options: readonly { id: string }[] | null, picked: string | null | undefined): string {
  if (!picked) return SALES_ALL_BUILDINGS;
  if (!options) return picked; // not loaded yet: ask for the remembered one; the API refuses a building I can't see
  return options.some(o => o.id === picked) ? picked : SALES_ALL_BUILDINGS;
}

export interface StaffingReport {
  from: string; to: string; location_id: string; location_name: string; benchmark: number; cost_hidden: boolean;
  hours: (StaffingHour & { date: string; hour: number; labour_cost_pence: number | null })[];
  days: { date: string; takings_pence: number; transactions: number; scheduled_hours: number; clocked_hours: number; under: number; over: number; labour_cost_pence: number | null; labour_pct: number | null }[];
  totals: { takings_pence: number; transactions: number; under: number; over: number; labour_cost_pence: number | null };
  suggestions: StaffingSuggestion[]; history_weeks: number;
}
export interface ShiftBonus {
  id: string; user_id: string; display_name: string; location_id: string; location_name: string; period: TargetPeriod; period_start: string;
  target_pence: number; takings_pence: number; paid_minutes: number; amount_pence: number | null; basis: string | null;
  status: 'pending' | 'approved' | 'rejected'; decided_by_name: string | null; decided_at: string | null; version: number;
}
export interface SalesTargetsBuilding {
  location_id: string; location_name: string; can_manage: boolean; has_tills: boolean;
  /** `day` is the daily target only when it's the same every day (else null); `day_targets` has the full week. */
  targets: Record<TargetPeriod, number | null>;
  /** t485u: daily target (pence) per ISO weekday, Monday first; null = no target that day. */
  day_targets: (number | null)[];
  bonus_rules: { id: string; period: TargetPeriod; kind: 'fixed' | 'percent'; amount_pence: number | null; percent: number | null; active: boolean }[];
}

/**
 * t469u: the workspace's Odoo connection (Settings > Odoo & sales targets). The API key is write-only: only its last
 * 4 characters come back. `source` says which details are in use: saved in the app, the server's fallback, demo tills
 * or nothing.
 */
export interface OdooConnectionView {
  source: 'settings' | 'server' | 'demo' | 'none';
  url: string; db: string; username: string;
  api_key_last4: string | null;
  /** True when every field is filled in (so the saved details are the ones used). */
  complete: boolean;
  /** The server's fallback host, when it has one and nothing is saved here. Never its credentials. */
  server_host: string | null;
  last_test_at: string | null; last_test_ok: boolean | null;
  updated_at: string | null; updated_by_name: string | null;
  can_store_secrets: boolean;
}
/** Omit a field to leave it alone; api_key null removes the saved key. `clear: true` removes the saved connection. */
export interface OdooConnectionInput { url?: string; db?: string; username?: string; api_key?: string | null; clear?: boolean }
export type OdooTestOutcome = 'connected' | 'wrong_credentials' | 'unreachable' | 'not_allowed' | 'not_set';
export interface OdooTestResult { ok: boolean; outcome: OdooTestOutcome; detail: string; pos_configs?: number }

// ---------------------------------------------------------------------------------------------
// t485u: Today's sales on a building page
// ---------------------------------------------------------------------------------------------
export interface SalesFigures { revenue_pence: number; orders: number; atv_pence: number | null; units: number }
export interface SalesFiguresChange { revenue: number | null; orders: number | null; atv: number | null; units: number | null }
export interface BuildingSalesToday {
  visible: true;
  location_id: string; location_name: string; timezone: string;
  /** Local date of "today". */
  date: string;
  /** Figures include orders up to this time (the last successful sync), and yesterday is cut at the same local time. */
  as_of: string | null;
  today: SalesFigures;
  /** Today's daily target (for today's weekday) and progress against it, if one is set. */
  target_pence: number | null; progress: number | null;
  yesterday_same_time: SalesFigures;
  /** Fractions (0.12 = +12%); null when yesterday had nothing to compare with. */
  change: SalesFiguresChange;
  /** The last 7 local days, oldest first; the last one is today (partial). */
  days: { date: string; revenue_pence: number; partial: boolean }[];
}
export type BuildingSalesPanel = { visible: false } | BuildingSalesToday;
export interface SalesHourInput { hour_start: string; takings_pence: number; transactions: number; units: number }

/**
 * t485u: today's revenue (VAT-inclusive, refunds netted off, the same basis as sales targets), orders (paid sales,
 * not refunds), ATV (revenue / orders) and units, against yesterday up to the same local time, and 7 days of revenue.
 * Hours are whole UTC hours, so the hour that holds yesterday's cut-off is counted pro rata.
 */
export function summariseBuildingSales(rows: SalesHourInput[], tz: string, now: Date, asOf: Date | null): Omit<BuildingSalesToday, 'visible' | 'location_id' | 'location_name' | 'timezone' | 'as_of' | 'target_pence' | 'progress'> {
  const nowL = DateTime.fromJSDate(now).setZone(tz);
  const todayStart = nowL.startOf('day');
  const asOfL = asOf ? DateTime.fromJSDate(asOf).setZone(tz) : null;
  // Yesterday at the same local clock time as the data (Luxon keeps the wall time across a clock change).
  const cut = (asOfL && asOfL >= todayStart && asOfL <= nowL ? asOfL : nowL).minus({ days: 1 });
  const yStart = todayStart.minus({ days: 1 });
  const zero = () => ({ revenue: 0, orders: 0, units: 0 });
  const t = zero(); const y = zero();
  const byDay = new Map<string, number>();
  for (const r of rows) {
    const at = DateTime.fromISO(r.hour_start, { zone: 'utc' }).setZone(tz);
    if (!at.isValid) continue;
    const day = at.toISODate()!;
    byDay.set(day, (byDay.get(day) ?? 0) + r.takings_pence);
    if (at >= todayStart) { t.revenue += r.takings_pence; t.orders += r.transactions; t.units += r.units; continue; }
    if (at < yStart || at >= cut) continue;
    const f = Math.min(1, cut.diff(at, 'milliseconds').milliseconds / 3_600_000);
    y.revenue += r.takings_pence * f; y.orders += r.transactions * f; y.units += r.units * f;
  }
  const figs = (v: ReturnType<typeof zero>): SalesFigures => {
    const revenue = Math.round(v.revenue); const orders = Math.round(v.orders);
    return { revenue_pence: revenue, orders, atv_pence: v.orders > 0 ? Math.round(v.revenue / v.orders) : null, units: Math.round(v.units * 100) / 100 };
  };
  const today = figs(t); const yesterday = figs(y);
  const ch = (a: number | null, b: number | null) => (a == null || b == null || b <= 0 ? null : Math.round(((a - b) / b) * 1000) / 1000);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = todayStart.minus({ days: 6 - i }).toISODate()!;
    return { date: d, revenue_pence: byDay.get(d) ?? 0, partial: i === 6 };
  });
  return {
    date: todayStart.toISODate()!, today, yesterday_same_time: yesterday,
    change: { revenue: ch(today.revenue_pence, yesterday.revenue_pence), orders: ch(today.orders, yesterday.orders), atv: ch(today.atv_pence, yesterday.atv_pence), units: ch(today.units, yesterday.units) },
    days,
  };
}

/** "▲ 12% vs this time yesterday", "Same as this time yesterday", or a plain line when there's nothing to compare. */
export function compareLine(today: number | null, yesterday: number | null): string {
  if (!yesterday || yesterday <= 0) return 'Nothing by this time yesterday';
  if (today == null) return 'None yet today';
  const pct = Math.round(((today - yesterday) / yesterday) * 100);
  if (pct === 0) return 'Same as this time yesterday';
  return `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}% vs this time yesterday`;
}

/** t485u: adds up buildings' figures (ATV is recomputed from the totals, not averaged). */
export function sumFigures(list: SalesFigures[]): SalesFigures {
  const revenue = list.reduce((a, f) => a + f.revenue_pence, 0);
  const orders = list.reduce((a, f) => a + f.orders, 0);
  return { revenue_pence: revenue, orders, atv_pence: orders > 0 ? Math.round(revenue / orders) : null, units: Math.round(list.reduce((a, f) => a + f.units, 0) * 100) / 100 };
}
export function figuresChange(today: SalesFigures, yesterday: SalesFigures): SalesFiguresChange {
  const ch = (a: number | null, b: number | null) => (a == null || b == null || b <= 0 ? null : Math.round(((a - b) / b) * 1000) / 1000);
  return { revenue: ch(today.revenue_pence, yesterday.revenue_pence), orders: ch(today.orders, yesterday.orders), atv: ch(today.atv_pence, yesterday.atv_pence), units: ch(today.units, yesterday.units) };
}

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
/** The daily target for a local date from a Monday-first week of targets. */
export const dayTargetFor = (dayTargets: (number | null)[], date: string): number | null => dayTargets[DateTime.fromISO(date, { zone: 'utc' }).weekday - 1] ?? null;
export const targetProgress = (takings: number, target: number | null) => (target ? Math.round((takings / target) * 1000) / 1000 : null);
